import React from "react"
import { graphql } from "gatsby"
import Layout from "components/Layout"
import SEO from "components/SEO"
import Content from "components/Content"
import Hero from "components/Hero"
import ResponsiveImage from "components/ResponsiveImage"
import Button from "components/Button"
import SliceZone from "components/SliceZone"
import { linkResolver } from "utils/linkResolver"
import { valuable } from "utils/worthy"

function Home({ ...props }) {
  const { index, site_settings } = props.data.prismic
  if (!index) return null
  const {
    meta_title,
    meta_description,
    meta_images,
    _meta: { lang },
  } = index
  const uri = linkResolver(index._meta)

  function renderHeaderButton() {
    if (index.cta_on_hero) {
      const { hero_cta_text, hero_cta_link } = index
      return (
        <Button
          forwardedAs="a"
          variant="primary"
          href={
            valuable(hero_cta_link)
              ? hero_cta_link.url || linkResolver(hero_cta_link._meta)
              : ""
          }
        >
          {hero_cta_text}
        </Button>
      )
    }
    return null
  }

  return (
    <>
      <SEO
        lang={lang}
        slug={uri}
        title={meta_title}
        description={meta_description}
        images={meta_images}
      />
      <Layout lang={lang} site_settings={site_settings} {...props}>
        <Hero
          bg={index.image_tone ? "primary" : "white"}
          color={
            index.color_theme ? "ice" : index.image_tone ? "white" : "primary"
          }
          align="center"
          bgImg={
            <ResponsiveImage
              img={index.imageSharp || index.image}
              alt={index.image.alt}
            />
          }
        >
          <article>
            <Content content={index.title} />
            <Content content={index.lead} />
            {renderHeaderButton()}
          </article>
        </Hero>
        <SliceZone slices={index.body} {...props}></SliceZone>
      </Layout>
    </>
  )
}

export default Home

Home.defaultProps = {}

Home.propTypes = {}

export const query = graphql`
  query PrismicIndex($lang: String!, $uid: String!, $isProduction: Boolean!) {
    prismic {
      site_settings(lang: $lang, uid: "site-settings") {
        footer_information
        ...FooterNavigation
        ...Navigation
        ...SocialMediaLinks
      }
      index(lang: $lang, uid: $uid) {
        _meta {
          lang
          uid
          type
          id
        }
        title
        lead
        color_theme
        cta_on_hero
        hero_cta_text
        hero_cta_link {
          ... on PRISMIC__ExternalLink {
            url
          }
          ... on PRISMIC_Page {
            _meta {
              uid
              type
              lang
            }
          }
        }
        image_tone
        image
        imageSharp @include(if: $isProduction) {
          childImageSharp {
            id
            main: fluid(maxWidth: 1920) {
              ...GatsbyImageSharpFluid_withWebp
            }
            mobile: fluid(maxWidth: 900) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
        }
        meta_title
        meta_description
        meta_images {
          image
          imageSharp {
            childImageSharp {
              id
              main: fixed(width: 1200) {
                ...GatsbyImageSharpFixed_withWebp
              }
            }
          }
        }
        hide_from_google
        body {
          ... on PRISMIC_IndexBodyCase_portfolio {
            type
            cases_list: fields {
              case_item: case {
                ... on PRISMIC_Case {
                  ...CaseFragment
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyCta {
            type
            primary {
              cta_title
              cta_content
              team_member {
                ... on PRISMIC_Team_member {
                  name
                  image
                  imageSharp {
                    childImageSharp {
                      id
                      desktop: fluid {
                        ...GatsbyImageSharpFluid_withWebp
                      }
                    }
                  }
                }
              }
            }
            fields {
              display_text
              primary
              link {
                ... on PRISMIC__ExternalLink {
                  url
                }
                ... on PRISMIC_Page {
                  _meta {
                    uid
                    type
                    lang
                  }
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyLogo_banner {
            type
            primary {
              logo_banner_lead
              content {
                ... on PRISMIC_Logo_banner {
                  ...LogoBanner
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyNewsletter {
            type
            primary {
              newsletter_title
              newsletter_content
              group_id
              group_option
              success_message
              error_message
              legal {
                ... on PRISMIC_Legal {
                  signup_disclaimer
                  signup_nudge
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyAbout_team {
            type
            primary {
              main_content
              technology_lead
              technology_text
              company_image
              company_imageSharp {
                publicURL
              }
              team_member_list {
                ... on PRISMIC_Team_member_list {
                  ...TeamMemberList
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyContent_block {
            type
            block: primary {
              is_primary
              heading
              content
            }
            fields {
              icon_image
              item_text
            }
          }
          ... on PRISMIC_IndexBodyContent_card {
            type
            primary {
              is_primary
              card_group_title
            }
            fields {
              card_icon
              card_title
              card_content
            }
          }
          ... on PRISMIC_IndexBodyCta_button {
            type
            primary {
              display_text
              link {
                ... on PRISMIC__ExternalLink {
                  url
                }
                ... on PRISMIC_Page {
                  _meta {
                    uid
                    type
                    lang
                  }
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyProduct {
            type
            primary {
              product {
                ... on PRISMIC_Product {
                  ...SingleProductFragment
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyProduct_grid {
            type
            label
            primary {
              product_grid_columns
              product_grid_content
              product_grid_title
            }
            products: fields {
              display_style
              product {
                ... on PRISMIC_Product {
                  ...ProductFragment
                }
              }
            }
          }
          ... on PRISMIC_IndexBodyArticle_gallery {
            type
          }
          ... on PRISMIC_IndexBodyContent {
            type
            primary {
              rich_text
              display_text
              link {
                ... on PRISMIC__ExternalLink {
                  url
                }
                ... on PRISMIC_Page {
                  _meta {
                    uid
                    type
                    lang
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
